import { ethers } from "ethers";
import Web3Modal from "web3modal";
import WalletConnect from "@walletconnect/web3-provider";
import CoinbaseWalletSDK from "@coinbase/wallet-sdk";
import { useEffect, useState } from "react";
import { GenericResponse } from "../networking/types";

type ConnectWalletResponse = {
  provider: ethers.providers.Web3Provider;
};

type AccountReponse = {
  account: string;
};

type GetAccountProps = {
  provider: ethers.providers.Web3Provider;
};
type SignedMessageProps = {
  provider: ethers.providers.Web3Provider;
  walletAddress: string;
  message: string;
  contentsHash: string;
};

type SignedMessageResponse = {
  signature: string;
};

const providerOptions = {
  walletlink: {
    package: CoinbaseWalletSDK,
    options: {
      appName: "web3_forms",
      infuraId: process.env.NEXT_PUBLIC_INFURA_ID,
    },
  },
  walletconnect: {
    package: WalletConnect,
    options: {
      infuraId: process.env.NEXT_PUBLIC_INFURA_ID,
    },
  },
};

type Web3Data = {
  connectWallet: () => Promise<GenericResponse<ConnectWalletResponse>>;
  getAccount: ({
    provider,
  }: GetAccountProps) => Promise<GenericResponse<AccountReponse>>;
  signMessage: ({
    message,
    walletAddress,
    contentsHash,
  }: SignedMessageProps) => Promise<GenericResponse<SignedMessageResponse>>;
};

const useWeb3Data = (): Web3Data => {
  const [web3Modal, setWeb3Modal] = useState<Web3Modal | undefined>();

  const clearWeb3ProviderCache = async () => {
    await web3Modal?.clearCachedProvider();
  };

  useEffect(() => {
    setWeb3Modal(
      new Web3Modal({
        network: "mainnet",
        cacheProvider: false,
        providerOptions,
      })
    );
    return () => {
      clearWeb3ProviderCache();
    };
  }, []);

  const connectWallet = async (): Promise<
    GenericResponse<ConnectWalletResponse>
  > => {
    try {
      const instance = await web3Modal?.connect();
      const provider = new ethers.providers.Web3Provider(instance);
      return {
        success: true,
        data: {
          provider,
        },
      };
    } catch (error) {
      await clearWeb3ProviderCache();
      return {
        success: false,
        error: "Failed to connect to the ethereum network",
      };
    }
  };

  const getAccount = async ({
    provider,
  }: GetAccountProps): Promise<GenericResponse<AccountReponse>> => {
    try {
      const accounts = await provider.listAccounts();
      if (accounts?.length == 0) {
        clearWeb3ProviderCache();
        return { success: false, error: "No account connected" };
      }
      return {
        success: true,
        data: {
          account: accounts![0],
        },
      };
    } catch (error: any) {
      await clearWeb3ProviderCache();
      if (error.message == "User rejected the request.") {
        return { success: false, error: "User rejected the integration" };
      }
      return { success: false, error: "An unknown error occured" };
    }
  };

  const signMessage = async ({
    provider,
    message,
    walletAddress,
    contentsHash,
  }: SignedMessageProps): Promise<GenericResponse<SignedMessageResponse>> => {
    try {
      const signature = await provider?.provider.request?.({
        method: "personal_sign",
        params: [message, walletAddress, contentsHash],
      });

      return {
        success: true,
        data: {
          signature,
        },
      };
    } catch (error) {
      await clearWeb3ProviderCache();
      return {
        success: false,
        error:
          "Signature denied: please sign the signature request when trying again.",
      };
    }
  };

  return {
    connectWallet,
    getAccount,
    signMessage,
  };
};

export { useWeb3Data };
